/**
 * One knock, one set of verbs, two surfaces.
 *
 * The card in the pane and the buttons on the desk are the same five actions on
 * the same knock. They are assembled here, once, and handed to both — because
 * `useInboxActions` holds the undo window and the in-flight flag in component
 * state, and two instances of it would be two undo windows for one action.
 *
 * The celebration hangs off the reply. Replying is what lets somebody through:
 * it is the moment the whole product exists for, and the only one in this file
 * that is allowed a noise.
 */
import { useMemo } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { RecipientKnock } from "../../lib/api";
import type { Conversation } from "../../lib/conversations";
import { celebrate } from "../../lib/celebrate";
import { qk } from "../../lib/queries";
import { useSessionExpired } from "../../lib/session";
import { openPanel, useInboxActions } from "../inbox/use-inbox-actions";
import type { CardHandlers, CardUi } from "../inbox/knock-card";
import { useActiveKnock } from "./use-active-knock";

export const THROUGH_STAMP = "Through";
export const THROUGH_BODY = "Your doorman stepped aside. This is the two of you now.";

/** Which of the card's four panels is showing, for the rail's own verbs. */
export type DeskPanel = ReturnType<typeof openPanel>;

export type InboxDesk = {
  knock: ReturnType<typeof useActiveKnock>["knock"];
  transcript: ReturnType<typeof useActiveKnock>["transcript"];
  failed: boolean;
  /** The knock is not at this door any more — blocked, or otherwise gone (b gap 4). */
  gone: boolean;
  reload: () => void;
  cardUi: CardUi;
  handlers: CardHandlers;
  /** The desk's two buttons: the same reply and the same dismissal. */
  accept: () => void;
  decline: () => void;
  /** Open on the card, if anything is. The rail greys its other verb while it is. */
  panel: DeskPanel;
  busy: boolean;
};

/** The empty state, so the desk can render before a knock has been read. */
const NO_CARD: CardUi = {
  expanded: true,
  composerOpen: false,
  replyDraft: "",
  dismissOpen: false,
  dismissOther: false,
  dismissNote: "",
  blockOpen: false,
  feedbackOpen: false,
  feedbackDone: null,
  pending: null,
  note: null,
  busy: false,
};

/**
 * SHOW THE OWNER WHAT THEIR BUTTON DID (owner, 2026-08-26: "it looks like the
 * button isn't working").
 *
 * The desk's two verbs are in the third column; what they change is the knock
 * card at the head of the thread scroller, one column over and possibly off the
 * top of it. Reply got there by accident — the composer's `autoFocus` drags the
 * viewport — and "Not for me" got there not at all, so pressing it moved
 * nothing anybody could see.
 *
 * ONE PLACE, and it is here rather than in each verb: the rail dispatches both
 * of them through `accept`/`decline`, so the reveal belongs on that seam. The
 * card publishes `data-knock-card` for exactly this and nothing else reads it.
 */
export function revealKnockCard(): void {
  if (typeof document === "undefined") return;
  const card = document.querySelector<HTMLElement>("[data-knock-card]");
  card?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * WHAT GOES STALE WHEN SOMETHING HAPPENS TO A KNOCK — one list, in one place.
 *
 * A verb moves the gate, so the sidebar's row, the briefing and the thread list
 * all have to hear about it. So does a message the owner writes to somebody
 * they have let in: it moves the thread's activity and its last line, which is
 * what the row draws. The owner's composer used to reload only the open knock,
 * and the sidebar then showed a stale thread until something else invalidated
 * it — so the list is named here and both callers use the same name.
 *
 * The knock ON SCREEN is not in this list: it is re-read by its own hook, and
 * the caller passes that reload in.
 */
export function staleAfterKnockChange(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: qk.recap() });
  void queryClient.invalidateQueries({ queryKey: ["inbox"] });
  void queryClient.invalidateQueries({ queryKey: qk.threads() });
}

export function useInboxDesk(conv: Conversation | undefined): InboxDesk {
  const queryClient = useQueryClient();
  const sessionExpired = useSessionExpired();
  const active = useActiveKnock(conv);
  const knockId = active.knockId;

  const actions = useInboxActions({
    onSessionExpired: sessionExpired,
    /**
     * Every verb here changes what the sidebar should say — a reply moves the
     * row from "waiting on you" to "let through", a dismissal takes it out of
     * the Passed count — so the list and the briefing are both re-read.
     */
    onActed: () => {
      // The briefing, the inbox and the list the sidebar draws — a verb moves
      // the thread's gate, so every one of them has to hear about it.
      staleAfterKnockChange(queryClient);
      // And the knock ON SCREEN re-reads itself (owner items 4+5, 2026-08-26):
      // the list invalidations update the sidebar's chip, but the desk's stamp
      // and the thread render `useActiveKnock`'s copy, which only re-read on a
      // conversation switch — so opening the door left the owner staring at the
      // old thread until they clicked away and back.
      active.reload();
    },
  });

  const card = knockId ? actions.state(knockId) : null;
  const knock = active.knock;

  return useMemo<InboxDesk>(() => {
    const id = knockId;
    const state = card ?? NO_CARD;

    const send = () => {
      if (!id) return;
      const body = state.replyDraft;
      if (!body.trim()) return;
      void actions.act(id, "reply", "reply", "Sent.", { body }).then(() => {
        celebrate({
          kind: "through",
          stamp: THROUGH_STAMP,
          title: `${firstWord(knock)} is in.`,
          body: THROUGH_BODY,
        });
      });
    };

    const handlers: CardHandlers = {
      onToggleExpand: () => undefined,
      onReply: () => id && actions.patch(id, { composerOpen: true, note: null }),
      onBook: () => void (id && actions.act(id, "book", "book", "Opened their link.")),
      onSave: () => void (id && actions.act(id, "save", "save", "Saved.")),
      onNotInterested: () => id && actions.patch(id, { dismissOpen: true }),
      /**
       * "Other" IS THE ONE CHIP THAT DOES NOT DISMISS. Every other reason is a
       * complete answer to "why is it not for you?" and the dismissal goes with
       * it; Other is the owner saying "none of these", which is not yet a
       * reason, so it opens one line and waits for Send. Empty is allowed there
       * — see `onSendDismissNote`.
       */
      onPickDismissReason: (chip) => {
        if (!id) return;
        if (chip === "other") {
          actions.patch(id, { dismissOther: true, dismissNote: "" });
          return;
        }
        void actions.act(id, "not_interested", "dismiss", "Dismissed.", { reason_chip: chip });
      },
      onDismissNote: (value) => id && actions.patch(id, { dismissNote: value }),
      onSendDismissNote: () => {
        if (!id) return;
        const body = state.dismissNote.trim();
        void actions.act(id, "not_interested", "dismiss", "Dismissed.", {
          reason_chip: "other",
          ...(body ? { body } : {}),
        });
      },
      onCancelDismiss: () => id && actions.patch(id, { dismissOpen: false }),
      onBlock: () => id && actions.patch(id, { blockOpen: true }),
      onConfirmBlock: () => void (id && actions.act(id, "block", "block", "Blocked.")),
      onCancelBlock: () => id && actions.patch(id, { blockOpen: false }),
      onReplyDraft: (value) => id && actions.patch(id, { replyDraft: value }),
      onSendReply: send,
      onCancelReply: () => id && actions.patch(id, { composerOpen: false, replyDraft: "" }),
      onHelpful: () => void (id && actions.feedback(id, true)),
      onNotHelpful: () => id && actions.patch(id, { feedbackOpen: true }),
      onPickFeedbackReason: (chip) => void (id && actions.feedback(id, false, chip)),
      onUndo: () => void (id && actions.undo(id)),
    };

    return {
      knock,
      transcript: active.transcript,
      failed: active.failed,
      gone: active.gone,
      reload: active.reload,
      cardUi: {
        expanded: true,
        composerOpen: state.composerOpen,
        replyDraft: state.replyDraft,
        dismissOpen: state.dismissOpen,
        dismissOther: state.dismissOther,
        dismissNote: state.dismissNote,
        blockOpen: state.blockOpen,
        feedbackOpen: state.feedbackOpen,
        feedbackDone: state.feedbackDone,
        // The presentational card never sees the undo token; the shell holds it.
        pending: state.pending ? { verb: state.pending.verb } : null,
        note: state.note,
        busy: state.busy,
      },
      handlers,
      /**
       * The desk's primary verb OPENS THE COMPOSER rather than sending
       * anything. Letting somebody through means saying something to them, and
       * a one-tap "accept" that posted an empty reply would be the product
       * speaking for the owner.
       *
       * Both of them then bring the card into view, because both of them
       * changed it and neither of them is on it (`revealKnockCard`).
       */
      accept: () => {
        if (!id) return;
        actions.patch(id, { composerOpen: true, note: null });
        revealKnockCard();
      },
      decline: () => {
        if (!id) return;
        actions.patch(id, { dismissOpen: true });
        revealKnockCard();
      },
      panel: openPanel(state),
      busy: state.busy,
    };
  }, [actions, active.failed, active.gone, active.reload, active.transcript, card, knock, knockId]);
}

function firstWord(knock: RecipientKnock | null): string {
  const shown = knock?.sender.display_name?.trim();
  if (shown) return shown.split(" ")[0] ?? shown;
  return knock?.sender.handle ? `@${knock.sender.handle}` : "They";
}
